import React, { useState } from 'react';
import {
  Package,
  Truck,
  ExternalLink,
  Copy,
  Download,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CreditCard,
  RefreshCw
} from 'lucide-react';
import { FirebaseOrder } from '@/models/order';
import { SavChat, ChatStatus } from '@/models/savChat';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { OrderRepository } from '@/services/firebase/order-repository';
import { FUNCTIONS_BASE_URL } from '@/shared/config/environment';
import { Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TailwindDialog, DialogContent, DialogActions, Button } from '@/components/ui';
import { Product } from '@/models/order';
import { useOrgaConfigurationContext } from '@/app/providers';
import { Wallet } from 'lucide-react';

// Mini Stepper - Styles compacts
const MiniStepIcon = styled('div')<{ ownerState: { active: boolean; completed: boolean; iconType: 'clock' | 'chef' | 'check' } }>(
  ({ ownerState }) => ({
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    position: 'relative',
    boxShadow: ownerState.active ? '0 2px 8px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.1)',
    backgroundColor: (() => {
      if (ownerState.active) {
        if (ownerState.iconType === 'clock') return 'var(--warning)';
        if (ownerState.iconType === 'chef') return 'var(--primary)';
        return 'var(--success)';
      }
      if (ownerState.completed) return 'var(--success)';
      return 'var(--border-medium)';
    })(),
    color: (ownerState.active || ownerState.completed) ? '#ffffff' : 'var(--foreground-secondary)',
    '&:hover': {
      transform: 'scale(1.15)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      backgroundColor: (() => {
        if (ownerState.iconType === 'clock') return 'var(--warning)';
        if (ownerState.iconType === 'chef') return 'var(--primary)';
        return 'var(--success)';
      })(),
      color: '#ffffff',
    },
    '&:active': {
      transform: 'scale(1.05)',
    }
  })
);

const CompactStepper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 0',

  '& .step-connector': {
    width: '16px',
    height: '2px',
    borderRadius: '1px',
    transition: 'background-color 0.3s ease',
    backgroundColor: 'var(--border-medium)',

    '&.active': {
      backgroundColor: 'var(--success)',
    }
  }
});

const MaterialIcon = ({ icon, className = "", style = {} }: { icon: string, className?: string, style?: React.CSSProperties }) => (
  <span className={`material-symbols-outlined ${className}`} style={style}>
    {icon}
  </span>
);

interface ChatInfoSidebarProps {
  chat: SavChat;
  order: FirebaseOrder | null;
  loadingOrder: boolean;
  organizationId: string;
  onUpdateStatus: (chatId: string, status: ChatStatus) => Promise<void>;
  isUpdatingStatus: boolean;
}

export const ChatInfoSidebar: React.FC<ChatInfoSidebarProps> = ({
  chat,
  order,
  loadingOrder,
}) => {
  const { t } = useTranslation();
  const { configuration } = useOrgaConfigurationContext();
  const [expandedPaymentDetails, setExpandedPaymentDetails] = useState<Set<number>>(new Set());

  const getPaymentProviderInfo = (method?: 'stripe' | 'paypal' | 'stripe_connect' | 'stripe_api_key' | string) => {
    if (method === 'paypal') {
      return {
        name: 'PayPal',
        color: 'text-[#003087] bg-[#003087]/10 border-[#003087]/20',
        icon: Wallet,
        idLabel: t('admin.savChat.admin.info.payment.providerInfo', { provider: 'PayPal' }),
        idLabelShort: 'TX'
      };
    }

    if (method === 'stripe_connect') {
      return {
        name: 'Stripe Connect',
        color: 'text-[#635BFF] bg-[#635BFF]/10 border-[#635BFF]/20',
        icon: CreditCard,
        idLabel: 'ID de transaction Stripe Connect (PI)',
        idLabelShort: 'PI'
      };
    }

    if (method === 'stripe_api_key') {
      return {
        name: 'Stripe API Key',
        color: 'text-[#4f46e5] bg-[#4f46e5]/10 border-[#4f46e5]/20', // Indigo pour différencier
        icon: CreditCard,
        idLabel: 'ID de transaction Stripe (PI)',
        idLabelShort: 'PI'
      };
    }

    // Le reste est fallback Stripe générique
    return {
      name: 'Stripe',
      color: 'text-[#635BFF] bg-[#635BFF]/10 border-[#635BFF]/20',
      icon: CreditCard,
      idLabel: t('admin.savChat.admin.info.payment.providerInfo', { provider: 'Stripe' }),
      idLabelShort: 'PI'
    };
  };

  // Refund modal state
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditProductIndex, setCreditProductIndex] = useState<number>(0);
  const [creditAmount, setCreditAmount] = useState<string>('');
  const [creditDescription, setCreditDescription] = useState<string>('');
  const [processingRefund, setProcessingRefund] = useState(false);
  const [includeShipping, setIncludeShipping] = useState(false);

  // Full Refund modal state
  const [showFullRefundModal, setShowFullRefundModal] = useState(false);
  const [fullRefundOrder, setFullRefundOrder] = useState<FirebaseOrder | null>(null);
  const [fullRefundIncludeShipping, setFullRefundIncludeShipping] = useState(false);

  // Validation logic for refund
  const getRefundValidation = (amountStr: string, index: number) => {
    if (!order?.products?.[index]) return { isValid: false, message: '' };

    const product = order.products[index];
    const productAmount = Number.parseFloat(product.amount || '0');
    const shippingCost = includeShipping ? (order.deliveryInfo?.shippingCost || 0) : 0;
    const totalAmount = productAmount + shippingCost;

    const alreadyRefundedInCents = product.amountRefunded || 0;
    const alreadyRefunded = typeof alreadyRefundedInCents === 'number'
      ? alreadyRefundedInCents / 100
      : Number.parseFloat((alreadyRefundedInCents as any).toString()) / 100;

    const maxRefundable = includeShipping
      ? totalAmount - alreadyRefunded
      : productAmount - alreadyRefunded;

    const amount = Number.parseFloat(amountStr);

    if (Number.isNaN(amount)) return { isValid: false, message: '' };
    if (amount <= 0) return { isValid: false, message: t('admin.savChat.admin.info.refundModal.validation.amountPositive') };
    if (amount > maxRefundable) {
      return {
        isValid: false,
        message: t('admin.savChat.admin.info.refundModal.validation.amountMax', { max: maxRefundable.toFixed(2) })
      };
    }

    return { isValid: true, message: '' };
  };

  const handleOpenRefundModal = (index: number) => {
    if (!order?.products?.[index]) return;

    setCreditProductIndex(index);
    setIncludeShipping(false);

    const product = order.products[index];
    const productAmount = Number.parseFloat(product.amount || '0');
    const alreadyRefundedInCents = product.amountRefunded || 0;
    const alreadyRefunded = typeof alreadyRefundedInCents === 'number'
      ? alreadyRefundedInCents / 100
      : Number.parseFloat((alreadyRefundedInCents as any).toString()) / 100;

    const remaining = Math.max(0, productAmount - alreadyRefunded);

    setCreditAmount(remaining > 0 ? remaining.toFixed(2) : '');
    setCreditDescription(t('admin.savChat.admin.info.payment.order') + ` #${order.id?.slice(-8) || ''}`);
    setShowCreditModal(true);
  };

  const handleRefundSubmit = async () => {
    if (processingRefund) return;
    if (!order || !creditAmount) return;

    const validation = getRefundValidation(creditAmount, creditProductIndex);
    if (!validation.isValid && validation.message) {
      toast.error(validation.message);
      return;
    }

    const amount = Number.parseFloat(creditAmount);
    const product = order.products[creditProductIndex];

    if (!product.paymentInfo?.paymentIntentId) {
      toast.error(t('admin.savChat.admin.info.payment.error'));
      return;
    }

    try {
      setProcessingRefund(true);

      const response = await fetch(`${FUNCTIONS_BASE_URL}/refundProduct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentIntentId: product.paymentInfo.paymentIntentId,
          ...(product.paymentInfo.chargeId && { chargeId: product.paymentInfo.chargeId }),
          amount,
          reason: creditDescription || `Remboursement SAV pour commande ${order.id?.slice(-8) || ''}`,
          orderId: order.id,
          productIndex: creditProductIndex,
          includeShipping: includeShipping,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors du remboursement');
      }

      toast.success(t('admin.savChat.admin.info.refundModal.success'));
      setShowCreditModal(false);
      // Note: L'interface ne se mettra pas à jour automatiquement car useOrder n'est pas realtime
      // Idéalement, il faudrait recharger la commande ici

    } catch (error: any) {
      console.error('Erreur refund:', error);
      toast.error(error.message || 'Erreur lors du remboursement');
    } finally {
      setProcessingRefund(false);
    }
  };

  // Rembourser toute la commande (tous les produits payés + éventuellement les frais de livraison)
  const handleFullOrderRefund = async (order: FirebaseOrder, includeShippingRequested: boolean) => {
    // Éviter les doubles clics
    if (processingRefund) {
      toast.error(t('admin.savChat.admin.info.refundModal.processing'));
      return;
    }

    try {
      setProcessingRefund(true);

      const response = await fetch(`${FUNCTIONS_BASE_URL}/refundFullOrder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderId: order.id,
          includeShipping: includeShippingRequested,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Erreur lors du remboursement complet de la commande');
      }

      const result = await response.json();
      toast.success(result.message || 'Remboursement complet effectué avec succès', { duration: 7000 });

      // Note: L'interface ne se mettra pas à jour automatiquement car useOrder n'est pas realtime

    } catch (error) {
      console.error('Erreur lors du remboursement complet de la commande:', error);
      toast.error(error instanceof Error ? error.message : 'Erreur lors du remboursement');
    } finally {
      setProcessingRefund(false);
    }
  };

  const handleCopyTracking = () => {
    if (order?.parcelGenerated?.trackingNumber) {
      navigator.clipboard.writeText(order.parcelGenerated.trackingNumber);
      toast.success(t('admin.savChat.admin.info.copyTracking'));
    }
  };


  const updateOrderPreparationStatus = async (orderId: string, status: 'todo' | 'in_progress' | 'ready') => {
    try {
      await OrderRepository.updateOrderPreparationStatus(orderId, status);
      toast.success(t('admin.savChat.admin.info.preparation.success')); // I need to add this key or use a generic one
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut de préparation:', error);
      toast.error('Erreur lors de la mise à jour du statut de préparation');
    }
  };


  const printLabel = async (order: any) => {
    const labelUrl = order.parcelGenerated?.labelPrinterUrl;
    if (!labelUrl) {
      toast.error('URL d\'étiquette non disponible');
      return;
    }

    try {
      const functionUrl = `${FUNCTIONS_BASE_URL}/downloadShippingLabel?orderId=${order.id}`;
      const response = await fetch(functionUrl, { method: 'GET' });
      if (!response.ok) throw new Error(`Failed to fetch label: ${response.status}`);
      const blob = await response.blob();
      const url = globalThis.URL.createObjectURL(blob);
      const printWindow = globalThis.open(url, '_blank', 'width=800,height=600');
      if (printWindow) {
        toast.success(t('admin.savChat.admin.info.print.opening')); // generic print success/opening
        printWindow.onload = () => { setTimeout(() => { printWindow.print(); }, 1000); };
        setTimeout(() => { if (!printWindow.closed) printWindow.print(); }, 2000);
        const checkClosed = setInterval(() => { if (printWindow.closed) { globalThis.URL.revokeObjectURL(url); clearInterval(checkClosed); } }, 1000);
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = `shipping-label-ORDER_${order.id?.slice(-8) || 'UNKNOWN'}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        globalThis.URL.revokeObjectURL(url);
        toast.success('Téléchargement de l\'étiquette');
      }
    } catch (error) {
      console.error('Erreur lors de l\'impression de l\'étiquette:', error);
      toast.error('Erreur lors de l\'impression de l\'étiquette');
    }
  };

  const downloadLabel = async (order: any) => {
    const labelUrl = order.deliveryInfo?.labelPrinterUrl;
    if (!labelUrl) {
      toast.error('URL d\'étiquette non disponible');
      return;
    }

    try {
      const functionUrl = `${FUNCTIONS_BASE_URL}/downloadShippingLabel?orderId=${order.id}`;
      const response = await fetch(functionUrl, { method: 'GET' });
      if (!response.ok) throw new Error(`Failed to fetch label: ${response.status}`);
      const blob = await response.blob();
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `shipping-label-ORDER_${order.id?.slice(-8) || 'UNKNOWN'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);
      toast.success(t('admin.savChat.admin.info.download.success'));
    } catch (error) {
      console.error('Erreur lors du téléchargement de l\'étiquette:', error);
      toast.error('Erreur lors du téléchargement de l\'étiquette');
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      if (typeof timestamp === 'string' && isNaN(date.getTime())) return timestamp;
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return typeof timestamp === 'string' ? timestamp : t('common.invalidDate');
    }
  };

  const getPaymentStatusColor = (status: 'pending' | 'paid' | 'failed' | 'canceled' | 'disputed' | 'refunded_partial' | 'refunded_full') => {
    switch (status) {
      case 'pending': return 'bg-warning-10 text-warning-dark';
      case 'paid': return 'bg-success-10 text-success-dark';
      case 'failed': return 'bg-error-10 text-error-dark';
      case 'canceled': return 'bg-background-tertiary text-foreground-secondary';
      case 'disputed': return 'bg-warning-10 text-warning-dark';
      case 'refunded_partial': return 'bg-warning-10 text-warning-dark';
      case 'refunded_full': return 'bg-error-10 text-error-dark';
      default: return 'bg-background-tertiary text-foreground-secondary';
    }
  };

  const getPaymentStatusText = (status: 'pending' | 'paid' | 'failed' | 'canceled' | 'disputed' | 'refunded_partial' | 'refunded_full') => {
    switch (status) {
      case 'pending': return t('orderDetails.pending');
      case 'paid': return t('orderDetails.paid');
      case 'failed': return t('customerOrders.paymentStatus.failed');
      case 'canceled': return t('customerOrders.paymentStatus.canceled');
      case 'disputed': return t('customerOrders.paymentStatus.disputed');
      case 'refunded_partial': return t('customerOrders.paymentStatus.refunded_partial');
      case 'refunded_full': return t('customerOrders.paymentStatus.refunded_full');
      default: return status;
    }
  };

  const togglePaymentDetailsExpanded = (index: number) => {
    setExpandedPaymentDetails(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(index)) {
        newExpanded.delete(index);
      } else {
        newExpanded.add(index);
      }
      return newExpanded;
    });
  };


  const hasLabelAction = !!order?.parcelGenerated?.labelPrinterUrl;
  const hasRefundAction = order?.products?.some((p) => p.paymentInfo?.status === 'paid' && p.paymentInfo?.method !== 'paypal') &&
    configuration?.paymentMode === 'stripe_api_key';
  const hasActions = hasLabelAction || hasRefundAction;

  return (
    <div className="w-full md:w-[360px] lg:w-[400px] flex-shrink-0 h-full md:border-l border-border-light bg-surface flex flex-col overflow-hidden">
      {/* Contenu scrollable */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="pt-4 px-4 pb-4 space-y-3">
          {/* Order info card */}
          <div className="bg-background rounded-lg p-2.5 space-y-2.5 text-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-foreground-secondary" />
                <span className="text-xs text-foreground-secondary">{t('admin.savChat.admin.info.order')}</span>
                <span className="font-mono text-sm font-medium">#{chat.orderReference}</span>
              </div>
              <span
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium ${t(`admin.savChat.status.${chat.status.toLowerCase()}`)}`}
              >
                {t(`admin.savChat.status.${chat.status.toLowerCase()}`)}
              </span>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-foreground-secondary uppercase">{t('admin.savChat.admin.info.reason')}</span>
                <span className="text-sm text-foreground">{t(`admin.savChat.reasons.${chat.reason}`)}</span>
              </div>
              {chat.reasonDetails && (
                <p className="text-xs text-foreground-secondary italic pl-0">"{chat.reasonDetails}"</p>
              )}
            </div>

            {!loadingOrder && order && order.products && order.products.length > 0 && (
              <>
                {/* Products - Section avec scroll interne si beaucoup de produits */}
                <div className="pt-2.5 border-t border-border-light space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-foreground-secondary uppercase">
                      {t('admin.savChat.admin.info.products')} ({order.products.length})
                    </p>
                    <p className="text-sm font-bold text-foreground">
                      {(order.products.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0) + (order.deliveryInfo?.shippingCost || 0)).toFixed(2)}€
                    </p>
                  </div>

                  {/* Liste des produits avec scroll si > 3 produits */}
                  <div className={`space-y-2 ${order.products.length > 3 ? 'max-h-[200px] overflow-y-auto custom-scrollbar pr-1' : ''}`}>
                    {order.products.map((product, idx) => (
                      <div key={idx} className="flex flex-col gap-1.5 p-2 bg-background-secondary rounded-lg">
                        <div className="flex justify-between text-sm">
                          <span className="text-foreground font-medium truncate mr-2">
                            {product.model}
                          </span>
                          <span className="font-bold whitespace-nowrap">{(parseFloat(product.amount) || 0).toFixed(2)}€</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-foreground-secondary">
                          <span>{t('orderDetails.product', { index: idx + 1 })}</span>
                        </div>

                        {/* Payment Status */}
                        <div className="flex items-center flex-wrap gap-1.5">
                          <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded-lg ${getPaymentStatusColor(product.paymentInfo.status)}`}>
                            {getPaymentStatusText(product.paymentInfo.status)}
                          </span>
                          {product.paymentInfo?.status === 'failed' && (product.paymentInfo as any).failureReason && (
                            <Tooltip title={(product.paymentInfo as any).failureReason} placement="top" arrow>
                              <AlertCircle className="h-3.5 w-3.5 text-error cursor-help" />
                            </Tooltip>
                          )}
                          {product.paymentInfo?.paymentIntentId && (
                            <button
                              onClick={() => togglePaymentDetailsExpanded(idx)}
                              className="flex items-center gap-1 text-[10px] font-bold text-foreground-secondary hover:text-foreground transition-colors"
                            >
                              {t('admin.savChat.admin.info.payment.details')}
                              {expandedPaymentDetails.has(idx) ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
                            </button>
                          )}
                          {/* Refund Button (Stripe Connect Only) */}
                          {product.paymentInfo?.status === 'paid' &&
                            product.paymentInfo?.method !== 'paypal' &&
                            (product.paymentInfo?.method === 'stripe_connect' || (product.paymentInfo?.method === 'stripe' && configuration?.paymentMode === 'stripe_connect')) && (
                              <button
                                onClick={() => handleOpenRefundModal(idx)}
                                className="flex items-center gap-1 text-[10px] font-bold text-primary hover:text-primary-dark transition-colors"
                                title={t('admin.savChat.admin.info.payment.refundTooltip')}
                              >
                                <RefreshCw className="w-2.5 h-2.5" />
                                {t('admin.savChat.admin.info.payment.refund')}
                              </button>
                            )}
                        </div>

                        {/* Payment Details (expandable) */}
                        {expandedPaymentDetails.has(idx) && (
                          <div className="p-2 bg-background-tertiary rounded-lg space-y-1 text-[10px] font-mono text-foreground mt-1">
                            <div className="mb-1 text-[9px] font-semibold text-foreground-secondary uppercase tracking-wide">
                              {t('admin.savChat.admin.info.payment.providerInfo', { provider: getPaymentProviderInfo(product.paymentInfo?.method || undefined).name })}
                            </div>
                            {product.paymentInfo?.paymentIntentId && (
                              <div className="flex justify-between">
                                <span className="text-foreground-secondary font-semibold">{getPaymentProviderInfo(product.paymentInfo.method || undefined).idLabelShort}:</span>
                                <span className="font-medium">{product.paymentInfo.paymentIntentId.slice(-8)}</span>
                              </div>
                            )}
                            {product.paymentInfo?.chargeId && (
                              <div className="flex justify-between">
                                <span className="text-foreground-secondary font-semibold">CH:</span>
                                <span className="font-medium">{product.paymentInfo.chargeId.slice(-8)}</span>
                              </div>
                            )}
                            {product.paymentInfo?.paidAt && (
                              <div className="flex justify-between pt-1 border-t border-border">
                                <span className="text-foreground-secondary">{t('admin.savChat.admin.info.payment.paidAt')}</span>
                                <span className="text-foreground font-medium">{formatDate(product.paymentInfo.paidAt)}</span>
                              </div>
                            )}

                            {/* Description pour le provider */}
                            <div className="pt-2 mt-2 border-t border-border">
                              <div className="mb-1 text-[9px] font-semibold text-foreground-secondary uppercase tracking-wide">
                                {t('admin.savChat.admin.info.payment.dashboardDescription', { provider: getPaymentProviderInfo(product.paymentInfo.method || undefined).name })}
                              </div>
                              <div className="text-[9px] text-foreground-secondary break-all leading-tight bg-background-secondary p-1.5 rounded select-all cursor-text">
                                {(() => {
                                  // Récupérer le nom du produit
                                  const productName = (product as any).name || product.model || product.reference || 'Produit';

                                  // Récupérer le montant net (en centimes) ou utiliser le montant brut comme fallback
                                  const netAmountInCents = product.paymentInfo?.netAmount
                                    ? (typeof product.paymentInfo.netAmount === 'number'
                                      ? product.paymentInfo.netAmount
                                      : Math.round(Number.parseFloat((product.paymentInfo.netAmount as any).toString()) * 100))
                                    : Math.round(Number.parseFloat(product.amount || '0') * 100);

                                  // Formater en euros
                                  const netAmountFormatted = (netAmountInCents / 100).toFixed(2);

                                  // Récupérer la devise (par défaut EUR)
                                  const currency = (product.paymentInfo?.currency || 'eur').toUpperCase();

                                  // Format simplifié : Nom du produit - Commande #ID - Montant EUR
                                  return `${productName} - ${t('admin.savChat.admin.info.payment.order')} #${order.id?.slice(-8) || order.id} - ${netAmountFormatted} ${currency}`;
                                })()}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Frais de livraison - Toujours visible en dehors du scroll */}
                  {order.deliveryInfo && typeof order.deliveryInfo.shippingCost === 'number' && order.deliveryInfo.shippingCost > 0 && (
                    <div className="flex justify-between items-center p-2 bg-background-tertiary rounded-lg text-xs">
                      <div className="flex items-center gap-2 text-foreground-secondary">
                        <Truck size={14} />
                        <span>{t('admin.savChat.admin.info.shippingFees')}</span>
                      </div>
                      <span className="font-bold">{(order.deliveryInfo.shippingCost).toFixed(2)}€</span>
                    </div>
                  )}
                </div>

                {/* Delivery info */}
                <div className="pt-2.5 border-t border-border-light">
                  <p className="text-xs font-semibold text-foreground-secondary uppercase mb-1.5">{t('admin.savChat.admin.info.delivery')}</p>
                  <div className="space-y-2">
                    {/* Transporteur & Tracking */}
                    <div className="flex items-start gap-2">
                      <Truck className="h-4 w-4 text-foreground-secondary flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between">
                          <p className="text-sm text-foreground font-medium">
                            {order.parcelGenerated?.carrierCode || order.deliveryInfo?.methodName || t('admin.savChat.admin.info.carrier')}
                          </p>
                        </div>

                        {order.parcelGenerated?.trackingNumber && (
                          <div className="flex items-center gap-2 mt-1">
                            <code className="bg-background-tertiary px-1.5 py-0.5 rounded text-xs font-mono text-foreground">
                              {order.parcelGenerated.trackingNumber}
                            </code>
                            <button
                              onClick={handleCopyTracking}
                              className="text-foreground-secondary hover:text-foreground transition-colors"
                              title={t('admin.savChat.admin.info.copyTracking')}
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                            {order.parcelGenerated?.trackingUrl && (
                              <a
                                href={order.parcelGenerated.trackingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:text-primary-dark transition-colors"
                                title={t('admin.savChat.admin.info.trackPackage')}
                              >
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Adresse / Point Relais - Version compacte */}
                    <div className="flex items-start gap-2">
                      <div className="w-4 flex justify-center flex-shrink-0 mt-0.5">
                        <span className="material-symbols-outlined text-foreground-secondary" style={{ fontSize: '16px' }}>location_on</span>
                      </div>
                      <div className="flex-1 min-w-0 text-xs">
                        {order.deliveryInfo?.pickupPoint ? (
                          <>
                            <p className="font-medium text-foreground">{t('admin.savChat.admin.info.address.pickupPoint')}</p>
                            <p className="text-foreground-secondary">{order.deliveryInfo.pickupPoint.name}</p>
                            <p className="text-foreground-secondary">
                              {order.deliveryInfo.pickupPoint.postalCode} {order.deliveryInfo.pickupPoint.city}
                            </p>
                          </>
                        ) : order.deliveryInfo?.address ? (
                          <>
                            <p className="font-medium text-foreground">{t('admin.savChat.admin.info.address.home')}</p>
                            <p className="text-foreground-secondary">
                              {order.orderInfo.firstName} {order.orderInfo.lastName}
                            </p>
                            <p className="text-foreground-secondary">
                              {order.deliveryInfo.postalCode} {order.deliveryInfo.city}
                            </p>
                          </>
                        ) : (
                          <p className="text-foreground-secondary italic">{t('admin.savChat.admin.info.address.notAvailable')}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preparation Status */}
                {order.deliveryInfo?.paymentStatus === 'paid' && (
                  <div className="pt-2.5 border-t border-border-light">
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-xs font-semibold text-foreground-secondary uppercase">{t('admin.savChat.admin.info.preparation.title')}</p>
                    </div>
                    <CompactStepper>
                      <Tooltip title={order.preparationStatus === undefined ? t('admin.savChat.admin.info.preparation.todo') : t('admin.savChat.admin.info.preparation.backToTodo')} placement="top" arrow>
                        <MiniStepIcon
                          ownerState={{
                            active: order.preparationStatus === 'todo' || !order.preparationStatus,
                            completed: order.preparationStatus === 'in_progress' || order.preparationStatus === 'ready',
                            iconType: 'clock'
                          }}
                          onClick={() => updateOrderPreparationStatus(order.id!, 'todo')}
                        >
                          <Clock size={12} />
                        </MiniStepIcon>
                      </Tooltip>

                      <div className={`step-connector ${order.preparationStatus === 'in_progress' || order.preparationStatus === 'ready' ? 'active' : ''}`} />

                      <Tooltip
                        title={
                          order.preparationStatus === 'in_progress'
                            ? t('admin.savChat.admin.info.preparation.inProgress')
                            : order.preparationStatus === 'ready'
                              ? t('admin.savChat.admin.info.preparation.backToInProgress')
                              : t('admin.savChat.admin.info.preparation.startPreparation')
                        }
                        placement="top"
                        arrow
                      >
                        <MiniStepIcon
                          ownerState={{
                            active: order.preparationStatus === 'in_progress',
                            completed: order.preparationStatus === 'ready',
                            iconType: 'chef'
                          }}
                          onClick={() => updateOrderPreparationStatus(order.id!, 'in_progress')}
                        >
                          <MaterialIcon icon="inventory_2" style={{ fontSize: '14px' }} />
                        </MiniStepIcon>
                      </Tooltip>

                      <div className={`step-connector ${order.preparationStatus === 'ready' ? 'active' : ''}`} />

                      <Tooltip
                        title={order.preparationStatus === 'ready' ? t('admin.savChat.admin.info.preparation.ready') : t('admin.savChat.admin.info.preparation.markAsReady')}
                        placement="top"
                        arrow
                      >
                        <MiniStepIcon
                          ownerState={{
                            active: order.preparationStatus === 'ready',
                            completed: false,
                            iconType: 'check'
                          }}
                          onClick={() => updateOrderPreparationStatus(order.id!, 'ready')}
                        >
                          <CheckCircle size={12} />
                        </MiniStepIcon>
                      </Tooltip>
                    </CompactStepper>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Action buttons - Footer fixe */}
      {hasActions && (
        <div className="flex-shrink-0 px-4 py-3 border-t border-border-light bg-surface">
          <p className="text-xs font-semibold text-foreground-secondary uppercase mb-2">{t('admin.savChat.admin.actions')}</p>
          <div className="flex flex-col gap-2">
            {/* Labels */}
            {hasLabelAction && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => printLabel(order)}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs bg-primary text-white hover:bg-primary-dark rounded-lg transition-colors font-medium"
                >
                  <Truck className="h-3.5 w-3.5" />
                  <span>{t('admin.dashboard.modal.printLabel')}</span>
                </button>

                <button
                  onClick={() => downloadLabel(order)}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs bg-background-tertiary text-foreground hover:bg-border-light rounded-lg transition-colors font-medium"
                >
                  <Download className="h-3.5 w-3.5" />
                  <span>{t('admin.dashboard.modal.downloadLabel')}</span>
                </button>
              </div>
            )}

            {/* Refund Full Order */}
            {hasRefundAction && (
              <button
                onClick={() => {
                  setFullRefundOrder(order);
                  setShowFullRefundModal(true);
                }}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs bg-error text-white hover:bg-error-dark rounded-lg transition-colors font-medium"
              >
                <CreditCard className="h-3.5 w-3.5" />
                <span>{t('admin.savChat.admin.info.payment.refundFullOrder')}</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Credit/Refund Modal */}
      <TailwindDialog
        isOpen={showCreditModal && !!order}
        onClose={() => {
          setShowCreditModal(false);
          setCreditAmount('');
          setCreditDescription('');
          setIncludeShipping(false);
        }}
        title={t('admin.savChat.admin.info.refundModal.title')}
        maxWidth="md"
      >
        {order && (
          <>
            <DialogContent>
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <CreditCard className="h-6 w-6 text-foreground-secondary" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-foreground-secondary">
                    {t('admin.savChat.admin.info.refundModal.refundFor')}{' '}
                    <span className="font-medium text-foreground">
                      {order.orderInfo.email}
                    </span>
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Shipping cost option */}
                {order.products?.[creditProductIndex] &&
                  order.products.length === 1 &&
                  order.deliveryInfo?.shippingCost &&
                  order.deliveryInfo.shippingCost > 0 &&
                  !order.deliveryInfo.sendcloudRefund &&
                  (() => {
                    const product = order.products[creditProductIndex];
                    const productAmount = Number.parseFloat(product.amount || '0');
                    const shippingCost = order.deliveryInfo.shippingCost || 0;
                    const totalAmount = productAmount + shippingCost;
                    const alreadyRefundedInCents = product.amountRefunded || 0;
                    const alreadyRefunded = typeof alreadyRefundedInCents === 'number'
                      ? alreadyRefundedInCents / 100
                      : Number.parseFloat((alreadyRefundedInCents as any).toString()) / 100;
                    const maxRefundableWithShipping = totalAmount - alreadyRefunded;

                    return (
                      <div className="mb-3 p-3 bg-primary-10 border border-primary-20 rounded-lg">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={includeShipping}
                            onChange={(e) => {
                              setIncludeShipping(e.target.checked);
                              if (e.target.checked) {
                                setCreditAmount(maxRefundableWithShipping > 0 ? maxRefundableWithShipping.toFixed(2) : totalAmount.toFixed(2));
                              } else {
                                const maxRefundableProduct = productAmount - alreadyRefunded;
                                setCreditAmount(maxRefundableProduct > 0 ? maxRefundableProduct.toFixed(2) : productAmount.toFixed(2));
                              }
                            }}
                            disabled={processingRefund}
                            className="mr-2 h-4 w-4 text-primary focus:ring-blue-500 border-border-medium rounded"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-foreground">
                              {t('admin.savChat.admin.info.refundModal.includeShipping')}
                            </span>
                            <p className="text-xs text-foreground-secondary mt-0.5">
                              Produit: €{productAmount.toFixed(2)} + Livraison: €{shippingCost.toFixed(2)}
                            </p>
                          </div>
                        </label>
                      </div>
                    );
                  })()}

                <div>
                  <label htmlFor="creditAmount" className="block text-sm font-medium text-foreground mb-1">
                    {t('admin.savChat.admin.info.refundModal.amount')}
                    {(() => {
                      const validation = getRefundValidation(creditAmount, creditProductIndex);
                      if (!validation.isValid && validation.message && Number.parseFloat(creditAmount) > 0) {
                        return <span className="text-error text-xs ml-2">{validation.message}</span>;
                      }
                      return null;
                    })()}
                  </label>
                  <input
                    type="number"
                    id="creditAmount"
                    step="0.01"
                    min="0.01"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                    disabled={processingRefund}
                    className="w-full px-3 py-2 border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-background-tertiary disabled:cursor-not-allowed"
                    placeholder="0.00"
                    autoFocus
                  />
                </div>

                <div>
                  <label htmlFor="creditDescription" className="block text-sm font-medium text-foreground mb-1">
                    {t('admin.savChat.admin.info.refundModal.description')}
                  </label>
                  <input
                    type="text"
                    id="creditDescription"
                    value={creditDescription}
                    onChange={(e) => setCreditDescription(e.target.value)}
                    disabled={processingRefund}
                    className="w-full px-3 py-2 border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-background-tertiary disabled:cursor-not-allowed"
                    placeholder={t('admin.savChat.admin.info.refundModal.descriptionPlaceholder')}
                  />
                </div>

                <div className="bg-warning-10 border border-warning-20 rounded-lg p-3">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-warning mr-2 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-warning-dark">
                      <p className="font-semibold mb-1">{t('admin.savChat.admin.info.payment.providerInfo', { provider: 'Stripe' })}</p>
                      <p className="text-xs">{t('admin.savChat.admin.info.payment.stripeDirectInfo')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                variant="ghost"
                size="md"
                onClick={() => setShowCreditModal(false)}
                disabled={processingRefund}
              >
                {t('admin.savChat.admin.info.refundModal.cancel')}
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleRefundSubmit}
                disabled={processingRefund || !creditAmount || Number.parseFloat(creditAmount) <= 0}
                isLoading={processingRefund}
              >
                {processingRefund ? t('admin.savChat.admin.info.refundModal.processing') : t('admin.savChat.admin.info.refundModal.confirm')}
              </Button>
            </DialogActions>
          </>
        )}
      </TailwindDialog>

      {/* Full Order Refund Confirmation Modal */}
      <TailwindDialog
        isOpen={showFullRefundModal && !!fullRefundOrder}
        onClose={() => {
          setShowFullRefundModal(false);
          setFullRefundOrder(null);
          setFullRefundIncludeShipping(false);
        }}
        title={t('admin.savChat.admin.info.payment.refundFullOrder')}
        maxWidth="md"
      >
        {fullRefundOrder && (
          <>
            <DialogContent>
              {(() => {
                const paidProducts = fullRefundOrder.products?.filter(
                  (p: Product) => p.paymentInfo?.status === 'paid'
                ) || [];
                const productsTotal = paidProducts.reduce(
                  (sum: number, p: Product) => sum + Number.parseFloat(p.amount || '0'),
                  0
                );
                const shippingCost = fullRefundOrder.deliveryInfo?.shippingCost || 0;
                const hasSendcloudRefund = !!fullRefundOrder.deliveryInfo?.sendcloudRefund;
                const canTryShippingRefund =
                  shippingCost > 0 && !hasSendcloudRefund;

                return (
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <AlertCircle className="h-6 w-6 text-error" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-foreground font-semibold">
                          {t('admin.savChat.admin.info.refundModal.fullRefundWarning')}
                        </p>
                        <p className="text-xs text-foreground-secondary mt-1">
                          {t('admin.savChat.admin.info.refundModal.fullRefundDisclaimer')}
                        </p>
                      </div>
                    </div>

                    <div className="bg-background-tertiary border border-border rounded-lg p-3 text-sm">
                      <div className="flex justify-between mb-1">
                        <span className="text-foreground-secondary">{t('admin.dashboard.modal.products')}:</span>
                        <span className="font-semibold text-foreground">
                          €{productsTotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-foreground-secondary">{t('admin.savChat.admin.info.shippingFees')}:</span>
                        <span className="font-semibold text-foreground">
                          €{shippingCost.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {canTryShippingRefund && (
                      <div className="p-3 bg-primary-10 border border-primary-20 rounded-lg">
                        <label className="flex items-start cursor-pointer gap-2">
                          <input
                            type="checkbox"
                            checked={fullRefundIncludeShipping}
                            onChange={(e) => setFullRefundIncludeShipping(e.target.checked)}
                            disabled={processingRefund}
                            className="mt-0.5 h-4 w-4 text-primary focus:ring-blue-500 border-border-medium rounded"
                          />
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {t('admin.savChat.admin.info.refundModal.tryShippingRefund')}
                            </p>
                            <p className="text-xs text-foreground-secondary mt-0.5">
                              {t('admin.savChat.admin.info.refundModal.shippingRefundInfo')}
                            </p>
                          </div>
                        </label>
                      </div>
                    )}

                    {hasSendcloudRefund && (
                      <div className="p-3 bg-warning-10 border border-warning-20 rounded-lg text-xs text-warning-dark">
                        {t('admin.savChat.admin.info.refundModal.shippingRefundAlreadyTried')}
                      </div>
                    )}
                  </div>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Button
                variant="ghost"
                size="md"
                onClick={() => {
                  setShowFullRefundModal(false);
                  setFullRefundOrder(null);
                  setFullRefundIncludeShipping(false);
                }}
                disabled={processingRefund}
              >
                {t('admin.savChat.admin.info.refundModal.cancel')}
              </Button>
              <Button
                variant="danger"
                size="md"
                onClick={async () => {
                  if (!fullRefundOrder || processingRefund) return;

                  const shippingCost = fullRefundOrder.deliveryInfo?.shippingCost || 0;
                  const hasSendcloudRefund = !!fullRefundOrder.deliveryInfo?.sendcloudRefund;
                  const canTryShippingRefund =
                    shippingCost > 0 && !hasSendcloudRefund;

                  await handleFullOrderRefund(
                    fullRefundOrder,
                    fullRefundIncludeShipping && canTryShippingRefund
                  );
                  setShowFullRefundModal(false);
                  setFullRefundOrder(null);
                  setFullRefundIncludeShipping(false);
                }}
                disabled={processingRefund}
                isLoading={processingRefund}
              >
                {processingRefund ? t('admin.savChat.admin.info.refundModal.processing') : t('admin.savChat.admin.info.refundModal.confirm')}
              </Button>
            </DialogActions>
          </>
        )}
      </TailwindDialog>
    </div>
  );
};

